import * as Api from '@ViewModels';
import { css } from 'aphrodite';
import { observer } from 'mobx-react';
import * as React from 'react';
import { IUser } from '../../../extViewmodels';
import { IModalContext } from '../../../models';
import { getDisplayName, getPrincipalInitials } from '../../../models/NameUtils';
import { isValidEmail } from '../../../models/UiUtils';
import { ActivationStatus, IUserPreferences } from '../../../viewmodels/AppViewModels';
import { Button } from '../../components-v2/Button';
import { DialogClose, DialogFooter } from '../../components-v2/Dialog';
import { Dropdown, DropdownContent, DropdownEllipsisTrigger, DropdownItem } from '../../components-v2/Dropdown';
import { Avatar2 } from '../../components/Avatar2';
import { DeprecatedTextArea } from '../../components/DeprecatedTextArea';
import { HelpTooltip } from '../../components/HelpTooltip';
import { LoadingSpinner } from '../../components/LoadingSpinner';
import { asModalComponent } from '../../components/Modal';
import { ISelectBoxOption, SelectBox } from '../../components/SelectBox';
import { EmailSignatureSettings } from '../../components/settings/PersonalSettings/EmailSignatureSettings';
import { SuccessCheckIcon } from '../../components/svgs/icons/SuccessCheckIcon';
import { TextInput } from '../../components/TextInput';
import { baseStyleSheet, bs } from '../../styles/styles';
import { ScheduleMeeting } from '../ScheduleMeeting';
import { useScheduleMeetingSettings } from '../ScheduleMeeting/useScheduleMeetingSettings';
import { styleSheet } from './styles';

const ManageUsersLicenseInfo = ({
	consumedUsers,
	isLoading,
	totalLicenses,
}: {
	consumedUsers: number;
	isLoading: boolean;
	totalLicenses: number;
}) => {
	if (!isLoading) {
		return (
			<p className={css(styleSheet.licenseCount)}>
				You have used <strong className={css(styleSheet.licenseCountStrong)}>{consumedUsers?.toString()}</strong> of{' '}
				{totalLicenses?.toString()} licenses. Please{' '}
				<a className='settings-contactSupportLink' href='mailto:support@levitateapp.com'>
					contact support
				</a>{' '}
				if you want to change your plan.
			</p>
		);
	}
	return <div>Loading user license information...</div>;
};

export const ManageUsersListViewHeader = ({
	consumedUsers,
	loading,
	totalLicenses,
	inviteClick,
}: {
	consumedUsers: number;
	loading: boolean;
	totalLicenses: number;
	inviteClick: () => void;
}) => {
	return (
		<div className='scroll-view-header-wrap'>
			<div>
				<ManageUsersLicenseInfo isLoading={loading} consumedUsers={consumedUsers} totalLicenses={totalLicenses} />
			</div>
			<div className={css(styleSheet.managedUsersActionBarContainer)}>
				<button className={css(baseStyleSheet.ctaButton)} onClick={inviteClick}>
					<span>Invite Employees</span>
				</button>
			</div>
			<div className={css(styleSheet.managedUsersEmployeeListHeader)}>
				<div className={css(styleSheet.headerLabel, styleSheet.headerAvatar)} />
				<div className={css(styleSheet.headerLabel, styleSheet.headerName)}>Employee</div>
				<div className={css(styleSheet.headerLabel, styleSheet.headerType)}>
					<div>Role</div>
					<HelpTooltip className={css(styleSheet.helpTooltip)}>
						<p className={css(styleSheet.headerTypeTooltipContent)}>
							Administrators have the ability to manage employees and groups, run reports, and see all contacts and
							notes in the system (including private notes).
						</p>
					</HelpTooltip>
				</div>
				<div className={css(styleSheet.headerLabel, styleSheet.headerStatus)}>Status</div>
				<div className={css(styleSheet.headerLabel, styleSheet.headerSignature)}>Signature</div>
				<div className={css(styleSheet.headerLabel, styleSheet.headerActions)}>Actions</div>
			</div>
		</div>
	);
};

const ManageUsersActionLink = ({
	deactivateClick,
	deleteClick,
	editCardSettingsClick,
	editMeetingLinksClick,
	editSignatureClick,
	editUserEmailClick,
	reactivateClick,
	user,
	userSession,
}: {
	deactivateClick: () => void;
	deleteClick: () => void;
	editCardSettingsClick: () => void;
	editMeetingLinksClick: () => void;
	editSignatureClick: () => void;
	editUserEmailClick: () => void;
	reactivateClick: () => void;
	user: IUser;
	userSession: Api.UserSessionContext;
}): JSX.Element => {
	const userActivationStatus = user.activationStatus;
	const userId = `action-${user.id}`;

	// Check if current user is a super admin
	const currentUserIsSuperAdmin = userSession.user?.role === 'superAdmin';
	// Check if the user being edited is a super admin
	const userIsSuperAdmin = user.role === 'superAdmin';

	// Administrators cannot edit super admin users
	if (userIsSuperAdmin && !currentUserIsSuperAdmin) {
		return <div className={css(styleSheet.employeeListItemActionItems)}>&mdash;</div>;
	}

	// Based on the user's activation status, formulate the right action link
	switch (userActivationStatus) {
		case ActivationStatus.ACTIVE:
			return (
				<div className={css(styleSheet.moreMenuWrap)}>
					<Dropdown>
						<DropdownEllipsisTrigger />
						<DropdownContent styles={[bs.w44]}>
							{user?.userMilestones?.hasSignatureTemplate ? (
								<DropdownItem onClick={editSignatureClick}>Edit Signature</DropdownItem>
							) : null}
							<DropdownItem onClick={editCardSettingsClick}>Edit Card Settings</DropdownItem>
							<DropdownItem onClick={editMeetingLinksClick}>Edit Meeting Links</DropdownItem>
							{userSession.account?.preferences?.allowChangeUserEmail ? (
								<DropdownItem onClick={editUserEmailClick}>Edit User Email</DropdownItem>
							) : null}
							<DropdownItem onClick={deactivateClick}>Deactivate User</DropdownItem>
						</DropdownContent>
					</Dropdown>
				</div>
			);
		case ActivationStatus.DEACTIVATED:
			return (
				<button
					type='button'
					id={userId}
					onClick={reactivateClick}
					className={css(styleSheet.actionLink, styleSheet.actionLinkActivate)}
				>
					Reactivate User
				</button>
			);
		case ActivationStatus.INIT:
			return (
				<button
					type='button'
					id={userId}
					onClick={deleteClick}
					className={css(styleSheet.actionLink, styleSheet.actionLinkDeactivate)}
				>
					Delete User
				</button>
			);
		default:
			return null;
	}
};

const UserTypeOptions: ISelectBoxOption<string>[] = [
	{
		title: 'Super Admin',
		value: 'superAdmin',
	},
	{
		title: 'Administrator',
		value: 'admin',
	},
	{
		title: 'User',
		value: 'user',
	},
	{
		title: 'Limited User',
		value: 'limitedUser',
	},
];

export const ManageUsersFormattedUser = ({
	deactivateClick,
	deleteClick,
	isInUpdatingList,
	onEditCardSettings,
	onEditEmailSignature,
	onEditMeetingLinks,
	onEditSignatureClick,
	onEditUserEmailClick,
	onUserRoleChanged,
	reactivateClick,
	user,
	userSession,
}: {
	deactivateClick: () => void;
	deleteClick: () => void;
	isInUpdatingList: boolean;
	onEditCardSettings: () => void;
	onEditEmailSignature: () => void;
	onEditMeetingLinks: () => void;
	onEditSignatureClick: () => void;
	onEditUserEmailClick: () => void;
	onUserRoleChanged: (role: ISelectBoxOption<string>) => void;
	reactivateClick: () => void;
	user: IUser;
	userSession: Api.UserSessionContext;
}): JSX.Element => {
	// Based on the user's activation status, formulate the right action link
	const activationStatus = user.activationStatus === ActivationStatus.INIT ? 'CREATED' : user.activationStatus;

	// Check if current user is a super admin
	const currentUserIsSuperAdmin = userSession.user?.role === 'superAdmin';
	// Check if the user being edited is a super admin
	const userIsSuperAdmin = user.role === 'superAdmin';
	// Administrators cannot edit super admin users
	const canEditRole = !userIsSuperAdmin || currentUserIsSuperAdmin;

	// Filter role options based on current user's role
	const availableRoleOptions = currentUserIsSuperAdmin
		? UserTypeOptions
		: UserTypeOptions.filter(option => option.value !== 'superAdmin');

	return (
		<div
			key={user.id}
			className={css(styleSheet.listItem, isInUpdatingList ? styleSheet.listItemLoading : null)}
			style={{ position: 'relative' }}
		>
			{isInUpdatingList ? (
				<div className={css(styleSheet.overlayContainer)}>
					<LoadingSpinner type='small' />
				</div>
			) : null}
			<>
				<div>
					<Avatar2
						styleDeclaration={styleSheet.avatar}
						fallbackText={getPrincipalInitials(user)}
						imgSrc={user?.profilePic}
					/>
				</div>
				<div className={css(styleSheet.employeeListItemName)}>
					<div>{getDisplayName(user)}</div>
					{!!user.primaryEmail && !!user.primaryEmail.value && (
						<div className={css(styleSheet.employeeListItemEmail)}>{user.primaryEmail.value}</div>
					)}
				</div>
				<div className={css(styleSheet.employeeListItemType)}>
					{canEditRole ? (
						<SelectBox
							triggerStyles={[styleSheet.selectBoxTrigger]}
							disabled={isInUpdatingList}
							onSelectionChanged={(role: ISelectBoxOption<string>) => onUserRoleChanged(role)}
							options={availableRoleOptions}
							selectedOption={
								availableRoleOptions.find(option => option.value === (user.role || 'user')) ||
								availableRoleOptions.find(option => option.value === 'user')
							}
						/>
					) : (
						<div className={css(styleSheet.employeeListItemRoleText)}>Super Admin</div>
					)}
				</div>
				<div className={css(styleSheet.employeeListItemStatus)}>{activationStatus}</div>
				<div className={css(styleSheet.employeeListItemSignature)}>
					{activationStatus !== ActivationStatus.DEACTIVATED ? (
						<div>
							{user?.userMilestones?.hasSignatureTemplate ? (
								<figure>
									<SuccessCheckIcon fillColor='#89C947' />
								</figure>
							) : canEditRole ? ( // If they can edit the role, they can add a signature
								<>
									<button
										type='button'
										className={css(styleSheet.employeeListItemSignatureAddButton)}
										onClick={onEditEmailSignature}
										tabIndex={0}
									>
										<div tabIndex={-1}>Add</div>
									</button>
								</>
							) : (
								<div>&mdash;</div> // Show dash for super admins when viewed by non-super admins
							)}
						</div>
					) : (
						<div>
							{' '}
							<div>&mdash;</div>{' '}
						</div>
					)}
				</div>
				<div className={css(styleSheet.employeeListItemActionItems)}>
					<ManageUsersActionLink
						deactivateClick={deactivateClick}
						deleteClick={deleteClick}
						editCardSettingsClick={onEditCardSettings}
						editMeetingLinksClick={onEditMeetingLinks}
						editSignatureClick={onEditSignatureClick}
						editUserEmailClick={onEditUserEmailClick}
						reactivateClick={reactivateClick}
						user={user}
						userSession={userSession}
					/>
				</div>
			</>
		</div>
	);
};

interface IManageUsersManagementModalProps extends IModalContext<IUser> {
	forUser: IUser;
	onRequestClose?: (result?: IUserPreferences, canceled?: boolean) => void;
}

const _SignatureSettings: React.FC<IManageUsersManagementModalProps> = ({ forUser }) => {
	return (
		<div className={css(styleSheet.manageUsersModalContainer, styleSheet.manageUsersModalContainerSignature)}>
			<EmailSignatureSettings forUser={forUser} />
		</div>
	);
};

export const _MeetingLinksSettings = ({
	forUser,
	onEditMeetingClicked,
}: {
	forUser: Api.IUser;
	onEditMeetingClicked: (meeting: Api.MeetingConfigViewModel) => void;
}) => {
	const { isLoadingMeetingLinks, meetingConfigs, onAddMeeting, onDeleteMeeting, onEditMeeting, onCloneMeeting } =
		useScheduleMeetingSettings({ onEditMeetingClicked, user: forUser });
	return (
		<div className={css(styleSheet.manageUsersModalContainer)}>
			<ScheduleMeeting
				isLoadingMeetingLinks={isLoadingMeetingLinks}
				meetingConfigs={meetingConfigs}
				onAddMeeting={onAddMeeting}
				onDeleteMeeting={onDeleteMeeting}
				onEditMeeting={onEditMeeting}
				onCloneMeeting={onCloneMeeting}
			/>
		</div>
	);
};

export const ManageUsersEditEmail = ({ onSave }: { onSave: (newEmail?: string, reason?: string) => void }) => {
	const [newEmail, setNewEmail] = React.useState('');
	const [reason, setReason] = React.useState('');

	return (
		<>
			<label htmlFor='edit-user-email' className={css(styleSheet.label)}>
				New Email:&nbsp;
				<span className={css(baseStyleSheet.required)}>*</span>
			</label>
			<TextInput
				className={css(styleSheet.inputs)}
				inputId='edit-user-email'
				onChange={(e: React.ChangeEvent<HTMLInputElement>) => setNewEmail(e.target.value)}
				type='text'
				value={newEmail}
			/>
			{!!newEmail && !isValidEmail(newEmail) ? (
				<span className={css(styleSheet.errorMessage)}>Please enter a valid email.</span>
			) : null}

			<label htmlFor='edit-user-email-reason' className={css(styleSheet.label)}>
				Reason for Change:&nbsp;
				<span className={css(baseStyleSheet.required)}>*</span>
			</label>
			<DeprecatedTextArea
				autoComplete='off'
				className={css(styleSheet.inputs)}
				inputId='edit-user-email-reason'
				onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setReason(e.target.value)}
				value={reason}
			/>
			{!!newEmail && !reason ? (
				<span className={css(styleSheet.errorMessage)}>Please enter a reason for the change.</span>
			) : null}

			<DialogFooter>
				<Button onClick={() => onSave(newEmail, reason)} disabled={!isValidEmail(newEmail) || !reason}>
					Change
				</Button>
				<DialogClose render={<Button variant='secondary'>Cancel</Button>} />
			</DialogFooter>
		</>
	);
};

export const ManageUsersEditSignature = asModalComponent(_SignatureSettings);

export const ManageUsersEditMeetingLinks = observer(_MeetingLinksSettings);
